import { createColumnHelper, ColumnDef, ColumnHelper } from "@tanstack/react-table";
import { Typography } from "components/miloDesignSystem/atoms/typography";
import { PropsWithChildren, useMemo } from "react";
import { textColumn } from "./columns/textColumn";
import { dateColumn } from "./columns/dateColumn";
import { numberColumn } from "./columns/numberColumn";
import { TypographyProps } from "components/miloDesignSystem/atoms/typography/types";

interface Options {
  shouldDisplayIndexColumn?: boolean;
}

interface EmptyValueProps {
  className?: string;
  fontSize?: TypographyProps["fontSize"];
  fontWeight?: TypographyProps["fontWeight"];
}

export function useCreateTableColumns<T>(
  getColumns: (args: {
    columnHelper: {
      accessor: ColumnHelper<T>["accessor"];
      display: ColumnHelper<T>["display"];
      group: ColumnHelper<T>["group"];
      text: typeof textColumn;
      date: typeof dateColumn;
      number: typeof numberColumn;
    };
  }) => ColumnDef<T, any>[],
  options?: Options,
) {
  return useMemo(() => {
    const baseColumnHelper = createColumnHelper<T>();
    const columns = getColumns({
      columnHelper: {
        ...baseColumnHelper,
        text: textColumn,
        date: dateColumn,
        number: numberColumn,
      },
    });
    if (options?.shouldDisplayIndexColumn) {
      return [
        baseColumnHelper.display({
          header: "#",
          size: 30,
          cell: props => (
            <BaseTableTypography>
              {props.row.depth ? "" : `${props.row.index + 1}.`}
            </BaseTableTypography>
          ),
        }),
        ...columns,
      ];
    }

    return columns;
  }, [getColumns, options?.shouldDisplayIndexColumn]);
}

const BaseTableTypography = ({ children }: PropsWithChildren<{}>) => {
  return (
    <Typography fontSize="12" fontWeight="700">
      {children}
    </Typography>
  );
};

export const EmptyValue = ({ className, fontSize = "12", fontWeight = "600" }: EmptyValueProps) => (
  <Typography className={className} fontSize={fontSize} fontWeight={fontWeight}>
    ---
  </Typography>
);
